import { useState, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection as fbCollection, doc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./config/firebase";
import { CSS } from "./styles/styles";
import { Icons } from "./components/Icons";
import { Toast } from "./components/Shared";
import { Dashboard, CollectionPage, CardDetailPage, LandingPage, AuthPage } from "./components/Pages";
import AddCardPage from "./components/AddCardPage";
import { CURRENCIES, fetchExchangeRates } from "./api/currencyService";

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState("landing");
  const [pageArg, setPageArg] = useState(null);
  const [collection, setCollection] = useState([]);
  const [toast, setToast] = useState(null);
  const [authMode, setAuthMode] = useState("login");

  // Currency state
  const [currency, setCurrency] = useState("USD");
  const [exchangeRates, setExchangeRates] = useState({ USD: 1 });

  // Global eBay price cache (persists across page navigations)
  const [ebayPriceCache, setEbayPriceCache] = useState({});
  const [collectionVersion, setCollectionVersion] = useState(0);

  // Fetch exchange rates on mount
  useEffect(() => {
    fetchExchangeRates().then(setExchangeRates);
  }, []);

  // Listen to Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          id: firebaseUser.uid,
          name: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User",
          email: firebaseUser.email,
          photoURL: firebaseUser.photoURL,
        });
        setPage("dashboard");
      } else {
        setUser(null);
        setPage("landing");
        setCollection([]);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Listen to Firestore collection in real-time
  useEffect(() => {
    if (!user) return;
    const colRef = fbCollection(db, "users", user.id, "collection");
    const unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCollection(items);
      },
      (err) => console.error("Firestore error:", err)
    );
    return () => unsubscribe();
  }, [user]);

  const navigate = (pg, arg = null) => { setPage(pg); setPageArg(arg); };
  const handleLogout = async () => { await signOut(auth); };

  const handleSaveCard = async (item) => {
    if (!user) return;
    try {
      const docRef = doc(db, "users", user.id, "collection", item.id);
      await setDoc(docRef, item);
      setToast({ message: collection.find((i) => i.id === item.id) ? "Card updated!" : "Card added to collection!", type: "success" });
      setCollectionVersion((v) => v + 1);
      navigate("collection");
    } catch (err) {
      setToast({ message: "Error saving card: " + err.message, type: "error" });
    }
  };

  const handleDelete = async (itemId) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, "users", user.id, "collection", itemId));
      setToast({ message: "Card removed from collection", type: "success" });
      setCollectionVersion((v) => v + 1);
    } catch (err) {
      setToast({ message: "Error removing card: " + err.message, type: "error" });
    }
  };

  const editItem = page === "edit" ? collection.find((i) => i.id === pageArg) : null;

  // Currency props passed to all pages
  const currencyProps = { currency, exchangeRates, ebayPriceCache, setEbayPriceCache, collectionVersion };

  if (authLoading) {
    return (<><style>{CSS}</style><div className="loading-screen"><Icons.Spinner /><p>Loading...</p></div></>);
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="app-container">
        {page === "landing" && !user && (
          <LandingPage onNavigate={(p) => { if (p === "login") { setAuthMode("login"); navigate("auth"); } else { setAuthMode("signup"); navigate("auth"); } }} />
        )}
        {(page === "auth" || page === "login" || page === "signup") && !user && (
          <AuthPage mode={page === "signup" ? "signup" : authMode} onToggle={() => setAuthMode((m) => (m === "login" ? "signup" : "login"))} />
        )}
        {user && !["landing", "auth", "login", "signup"].includes(page) && (
          <div className="app-layout">
            <aside className="sidebar">
              <div className="sidebar-brand" onClick={() => navigate("dashboard")}>
                <img src="/logos/honki-logo.png" alt="Honki TCG" style={{ height: 56 }} />
              </div>
              <div className="sidebar-nav">
                <div className="sidebar-section">Menu</div>
                <button className={`sidebar-link ${page === "dashboard" ? "active" : ""}`} onClick={() => navigate("dashboard")}><Icons.Home /> Dashboard</button>
                <button className={`sidebar-link ${page === "collection" ? "active" : ""}`} onClick={() => navigate("collection")}><Icons.Collection /> Collection</button>
                <button className={`sidebar-link ${page === "add" ? "active" : ""}`} onClick={() => navigate("add")}><Icons.Plus /> Add Card</button>

                {/* Currency Selector */}
                <div className="sidebar-section" style={{ marginTop: 12 }}>Currency</div>
                <div style={{ padding: "0 14px" }}>
                  <select
                    className="input"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={{ padding: "8px 10px", fontSize: 13, background: "var(--bg-input)" }}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
                    ))}
                  </select>
                  {currency !== "USD" && exchangeRates[currency] && (
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6, padding: "0 2px" }}>
                      1 USD = {exchangeRates[currency].toFixed(currency === "JPY" || currency === "KRW" ? 0 : 2)} {currency}
                    </div>
                  )}
                </div>
              </div>
              <div className="sidebar-footer">
                <div className="sidebar-user">
                  <div className="sidebar-avatar">
                    {user.photoURL ? <img src={user.photoURL} alt="" /> : (user.name || "U")[0].toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="sidebar-username">{user.name}</div>
                    <div className="sidebar-email">{user.email}</div>
                  </div>
                </div>
                <button className="nav-logout" onClick={handleLogout}><Icons.Logout /> Logout</button>
              </div>
            </aside>
            <main className="content">
              {page === "dashboard" && <Dashboard collection={collection} onNavigate={navigate} {...currencyProps} />}
              {page === "collection" && <CollectionPage collection={collection} onNavigate={navigate} onDelete={handleDelete} {...currencyProps} />}
              {page === "add" && <AddCardPage onSave={handleSaveCard} onNavigate={navigate} {...currencyProps} />}
              {page === "edit" && editItem && <AddCardPage onSave={handleSaveCard} onNavigate={navigate} editItem={editItem} {...currencyProps} />}
              {page === "detail" && <CardDetailPage cardId={pageArg} collection={collection} onNavigate={navigate} {...currencyProps} />}
            </main>

            {/* Mobile Top Bar */}
            <div className="mobile-topbar">
              <img src="/logos/honki-logo.png" alt="Honki TCG" style={{ height: 28 }} />
              <select
                className="mobile-currency-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
            </div>

            {/* Mobile Bottom Navigation */}
            <nav className="mobile-nav">
              <button className={`mobile-nav-btn ${page === "dashboard" ? "active" : ""}`} onClick={() => navigate("dashboard")}>
                <Icons.Home /><span>Dashboard</span>
              </button>
              <button className={`mobile-nav-btn ${page === "collection" ? "active" : ""}`} onClick={() => navigate("collection")}>
                <Icons.Collection /><span>Collection</span>
              </button>
              <button className={`mobile-nav-btn ${page === "add" || page === "edit" ? "active" : ""}`} onClick={() => navigate("add")}>
                <Icons.Plus /><span>Add Card</span>
              </button>
              <button className="mobile-nav-btn" onClick={handleLogout}>
                <Icons.Logout /><span>Logout</span>
              </button>
            </nav>
          </div>
        )}
      </div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </>
  );
}
